// src/pages/Accounting/Reports/AccountList/index.tsx
// Route: /accounting/reports/account-list
// Opens when the user clicks "Run report" on the Chart of accounts page.
// Composes: ReportHeader + ReportToolbar + ReportBody (print-area) + all modals and drawers.
// AccountListReportHeader is exported for the AppLayout pageHeader sticky slot
// (back link in Novala teal + breadcrumb Accounting / Chart of accounts / Account list).

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccountListReport } from '../../../../hooks/useAccountListReport';
import { useReportCustomization } from '../../../../hooks/useReportCustomization';
import { useSavedReports } from '../../../../hooks/useSavedReports';
import { useScheduledReports } from '../../../../hooks/useScheduledReports';
import { useManagementReports } from '../../../../hooks/useManagementReports';
import { printReport } from '../../../../utils/printReport';
import ExportFormatPicker from '../../../../components/common/ExportFormatPicker';
import ReportHeader from './components/ReportHeader';
import ReportToolbar from './components/ReportToolbar';
import ReportBody from './components/ReportBody';
import LoadingState from './components/LoadingState';
import EmptyState from './components/EmptyState';
import ErrorState from './components/ErrorState';
import EmailModal from './components/EmailModal';
import SaveAsModal from './components/SaveAsModal';
import ScheduleReportModal from './components/ScheduleReportModal';
import AddToMgmtReportsModal from './components/AddToMgmtReportsModal';
import AskVelaPanel from './components/AskVelaPanel';
import CustomizeDrawer from './components/CustomizeDrawer';
import { visibleColumns, applyCustomization } from './utils';
import { DEFAULT_COLUMNS } from './constants';
import type { EmailParams } from './types';

const REPORT_NAME = 'Account List';
const REPORT_KEY = 'account-list';

function readUserName(): string | null {
  try {
    const n = localStorage.getItem('full_name') || localStorage.getItem('first_name') || null;
    return n;
  } catch (e) {
    return null;
  }
}

export function AccountListReportHeader() {
  const navigate = useNavigate();
  return (
    <div className="px-4 py-3.5 md:px-8">
      <button
        type="button"
        onClick={() => navigate('/accounting/chart-of-accounts')}
        className="inline-flex items-center gap-0.5 text-[12.5px] font-semibold bg-transparent border-none p-0 cursor-pointer hover:underline mb-1.5"
        style={{ color: '#0F6E56' }}
      >
        <span className="text-[15px] leading-none mr-0.5">‹</span> Back to chart of accounts
      </button>
      <div className="text-[12.5px] text-ink3 font-medium">
        <span className="cursor-pointer hover:text-ink" onClick={() => navigate('/accounting')}>Accounting</span>
        <span className="text-ink3 mx-1.5">/</span>
        <span className="cursor-pointer hover:text-ink" onClick={() => navigate('/accounting/chart-of-accounts')}>Chart of accounts</span>
        <span className="text-ink3 mx-1.5">/</span>
        <span>Account list</span>
      </div>
    </div>
  );
}

export default function AccountListReportPage() {
  const { data, loading, error, refetch, lastRefreshedAt } = useAccountListReport();
  const { customization, draft, isCustomized, updateDraft, applyChanges, resetToDefault, cancelDraft } = useReportCustomization(REPORT_KEY);
  const { saveReport } = useSavedReports();
  const { addSchedule } = useScheduledReports();
  const { addToManagement } = useManagementReports();

  const [density, setDensity] = useState<'compact' | 'standard' | 'spacious'>('compact');
  const [zoom, setZoom] = useState(100);

  const [emailOpen, setEmailOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [addMgmtOpen, setAddMgmtOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [velaOpen, setVelaOpen] = useState(false);

  const userName = readUserName();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

  const exportOptions = useMemo(() => {
    if (!data) return null;
    const activeColumns = visibleColumns(customization);
    const colDefMap = Object.fromEntries(DEFAULT_COLUMNS.map((c) => [c.key, c]));
    const rows = applyCustomization(data.rows, customization);
    return {
      reportName: REPORT_NAME,
      companyName: data.companyName,
      subtitle: REPORT_NAME,
      columns: activeColumns.map((c) => ({
        key: c.key as string,
        label: colDefMap[c.key as string]?.label ?? (c.key as string),
        align: (colDefMap[c.key as string]?.align === 'right' ? 'right' : 'left') as 'left' | 'right',
      })),
      rows: rows.map((r) => r as unknown as Record<string, string | number | null>),
    };
  }, [data, customization]);

  const handlePrint = () => printReport({ documentTitle: REPORT_NAME });

  const handleSendEmail = (params: EmailParams) => {
    alert(`Email queued to: ${params.to}\nFormat: ${params.format}\nFile: ${params.fileName}${params.format === 'xlsx' ? '.xlsx' : params.format === 'csv' ? '.csv' : '.pdf'}`);
    setEmailOpen(false);
  };

  const handleSaveReport = (input: { name: string; shareWith: 'none' | 'everyone' | string; group: string | null }) => {
    saveReport({ ...input, customization });
    setSaveAsOpen(false);
    alert('Report saved: ' + input.name);
  };

  const handleSchedule = (input: Parameters<typeof addSchedule>[0]) => {
    addSchedule(input);
    setScheduleOpen(false);
    alert('Report scheduled.');
  };

  const handleAddToMgmt = (group: string) => {
    addToManagement({ reportKey: REPORT_KEY, reportName: REPORT_NAME, group });
    setAddMgmtOpen(false);
    alert('Added to Management reports.');
  };

  const handleAskVela = (prompt: string) => {
    alert('Vela answer for: ' + prompt + '\n\n(Backend lands in a follow-up.)');
  };

  return (
    <div className="max-w-full m-0 px-4 pb-24 bg-white md:px-8 min-h-full">
      {/* STICKY TOP (toolbar area; back link + breadcrumb live in the AppLayout pageHeader slot above) */}
      <div className="sticky top-0 z-20 bg-white -mx-4 md:-mx-8 px-4 md:px-8 pt-2 pb-3 no-print">
        <ReportHeader
          velaCount={3}
          onVelaClick={() => setVelaOpen(true)}
          isCustomized={isCustomized}
          onCustomize={() => setCustomizeOpen(true)}
          onSaveAs={() => setSaveAsOpen(true)}
          onLearnMore={() => window.open('https://getnovala.com/help/reports/account-list', '_blank')}
          onGiveFeedback={() => alert('Feedback flow (follow-up PR)')}
        />
        <ReportToolbar
          lastRefreshedAt={lastRefreshedAt}
          isRefreshing={loading}
          density={density}
          zoom={zoom}
          onDensityChange={setDensity}
          onZoomChange={setZoom}
          onRefresh={refetch}
          onEmail={() => setEmailOpen(true)}
          onPrint={handlePrint}
          onExport={() => setExportOpen(true)}
          onScheduleReport={() => setScheduleOpen(true)}
          onAddToManagementReports={() => setAddMgmtOpen(true)}
          onAskVela={() => setVelaOpen(true)}
        />
      </div>

      {/* BODY */}
      <div className="pt-6">
        {loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState onRetry={refetch} />
        ) : !data || data.rows.length === 0 ? (
          <EmptyState />
        ) : (
          <ReportBody data={data} customization={customization} density={density} zoom={zoom} />
        )}
      </div>

      {/* Modals and drawers */}
      {data && (
        <>
          <EmailModal
            open={emailOpen}
            onClose={() => setEmailOpen(false)}
            onSend={handleSendEmail}
            reportName={REPORT_NAME}
            companyName={data.companyName}
            userName={userName}
          />
          <SaveAsModal
            open={saveAsOpen}
            onClose={() => setSaveAsOpen(false)}
            onSave={handleSaveReport}
            defaultName={REPORT_NAME + ' - copy'}
          />
          <ScheduleReportModal
            open={scheduleOpen}
            onClose={() => setScheduleOpen(false)}
            onSchedule={handleSchedule}
            reportName={REPORT_NAME}
            defaultTimezone={timezone}
          />
          <AddToMgmtReportsModal
            open={addMgmtOpen}
            onClose={() => setAddMgmtOpen(false)}
            onAdd={handleAddToMgmt}
            reportName={REPORT_NAME}
          />
          <AskVelaPanel
            open={velaOpen}
            onClose={() => setVelaOpen(false)}
            onAsk={handleAskVela}
          />
          <CustomizeDrawer
            open={customizeOpen}
            onClose={() => setCustomizeOpen(false)}
            draft={draft}
            updateDraft={updateDraft}
            onApply={applyChanges}
            onResetToDefault={resetToDefault}
            onCancel={cancelDraft}
          />
          {exportOptions && (
            <ExportFormatPicker
              open={exportOpen}
              onClose={() => setExportOpen(false)}
              reportName={REPORT_NAME}
              options={exportOptions}
            />
          )}
        </>
      )}
    </div>
  );
}